import React, { useMemo, useCallback, memo } from "react";
import {
  ResponsiveFunnel,
  FunnelCustomLayerProps,
  FunnelPartWithHandlers,
  FunnelAreaGenerator
} from "@nivo/funnel";
import { getColorForMilestoneByType } from "@/components/shared/Milestones";
import { ConversionRatesChartDataType } from "@/components/layouts/ConversionRates/ConversionRates";
import { isEmpty, map, isEqual } from "lodash";

type ConversionRatesChartProps = {
  data: ConversionRatesChartDataType[];
  selectedStageId?: string;
};

type AdditionalBorderLayerProps = {
  parts: FunnelPartWithHandlers<ConversionRatesChartDataType>[];
  selectedStageId?: string;
  areaGenerator: FunnelAreaGenerator;
};

const AdditionalBorderLayer: React.FC<AdditionalBorderLayerProps> = ({
  parts,
  selectedStageId,
  areaGenerator
}) => {
  return (
    <g>
      {parts.map((part) => {
        if (part.data.id === selectedStageId) {
          const areaPath = areaGenerator(part.areaPoints);
          if (!areaPath) return null;

          return (
            <path
              key={part.data.id}
              d={areaPath}
              fill="none"
              stroke="white"
              strokeWidth={2}
            />
          );
        }
        return null;
      })}
    </g>
  );
};

const ConversionRatesChartInner: React.FC<ConversionRatesChartProps> = ({
  data,
  selectedStageId
}) => {
  const colors = useMemo(
    () => map(data, (stage) => getColorForMilestoneByType(stage.milestone)),
    [data]
  );

  // Memoize the custom layer function to prevent memory leaks
  // Nivo stores layer references internally, so new functions cause accumulation
  const customBorderLayer = useCallback(
    ({
      parts,
      areaGenerator
    }: FunnelCustomLayerProps<ConversionRatesChartDataType>) => (
      <AdditionalBorderLayer
        parts={parts}
        selectedStageId={selectedStageId}
        areaGenerator={areaGenerator}
      />
    ),
    [selectedStageId]
  );

  const layers = useMemo(
    () => [
      "separators" as const,
      "parts" as const,
      customBorderLayer,
      "labels" as const,
      "annotations" as const
    ],
    [customBorderLayer]
  );

  const getBorderColor = useCallback(
    (datum: any) => getColorForMilestoneByType(datum.data.milestone, "border"),
    []
  );

  const margin = useMemo(
    () => ({ top: 0, right: 50, bottom: 0, left: 50 }),
    []
  );

  if (isEmpty(data)) return null;

  return (
    <ResponsiveFunnel
      data={data}
      colors={colors}
      borderColor={getBorderColor}
      margin={margin}
      shapeBlending={0.3}
      interpolation="smooth"
      spacing={2}
      borderWidth={18}
      enableBeforeSeparators={false}
      enableAfterSeparators={false}
      isInteractive={false}
      animate={false}
      layers={layers}
    />
  );
};

// Wrap with React.memo and custom comparison to prevent unnecessary re-renders
const ConversionRatesChart = memo(
  ConversionRatesChartInner,
  (prevProps, nextProps) => {
    // Only re-render if data or selectedStageId actually changed
    return (
      isEqual(prevProps.data, nextProps.data) &&
      prevProps.selectedStageId === nextProps.selectedStageId
    );
  }
);

export default ConversionRatesChart;
